import { Link as RouterLink, useInRouterContext } from "react-router-dom";
import { clsx } from "clsx";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import { MouseEventHandler, ReactNode } from "react";
import { assertNotUndefined } from "@/util/typeguards";
import { isHttpUrl } from "@/util/url.ts";

export default function Link({
    children,
    to,
    style,
    className,
    external = false,
    inNewTab = true,
    onClick,
    quiet = false,
}: {
    children: ReactNode;
    to?: string;
    style?: React.CSSProperties;
    onClick?: MouseEventHandler<HTMLSpanElement>;
    className?: string;
    external?: boolean;
    inNewTab?: boolean;
    /** Grey, darkening on hover: "All sources", "Installation guide". */
    quiet?: boolean;
}) {
    const isInRouterContext = useInRouterContext();
    // An external link takes the colour of the text it sits in, and darkens
    // on hover as a quiet one does
    className = clsx(
        (quiet || external) && "hover-emphasize-text-subtle",
        quiet && "text-subtle",
        className,
    );

    if (onClick && external) {
        throw new Error("External links cannot have onClick handlers");
    }

    if (!to && !onClick) {
        throw new Error("Link must have either to or onClick prop");
    }

    if (onClick) {
        return (
            <span
                style={style}
                onClick={onClick}
                className={clsx("cursor-pointer", className)}
            >
                {children}
            </span>
        );
    }

    if (external) {
        // Defense in depth: never render a clickable link for non-http(s)
        // schemes (e.g. javascript:) even if an unsafe href slips through.
        if (to && !isHttpUrl(to)) {
            return (
                <span style={style} className={className}>
                    {children}
                </span>
            );
        }

        return (
            <a
                href={to}
                style={style}
                target={inNewTab ? "_blank" : undefined}
                rel="noreferrer"
                className={clsx("cursor-pointer underline", className)}
            >
                {children}
                <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2 inline-block" />
            </a>
        );
    }

    assertNotUndefined(to);

    if (!isInRouterContext) {
        return (
            <a href={to} className={clsx("cursor-pointer", className)}>
                {children}
            </a>
        );
    }

    return (
        <RouterLink to={to} className={clsx("cursor-pointer", className)}>
            {children}
        </RouterLink>
    );
}
